import React, { useState } from 'react';
import axios from 'axios';
import { Check, Send } from 'lucide-react';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

/** Shared lead form used by Contact, Book-Demo, and Custom-Build pages. */
export default function LeadForm({ kind, slug, tierId, fields = ['company', 'phone', 'message'], ctaLabel = 'Send message', successTitle, testIdPrefix = 'lead-form' }) {
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', message: '', budget: '', timeline: '', preferred_time: '' });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await axios.post(`${API}/systems/leads`, {
        kind,
        slug: slug || null,
        tier_id: tierId || null,
        name: form.name,
        email: form.email,
        company: form.company || null,
        phone: form.phone || null,
        message: form.message,
        budget: form.budget || null,
        timeline: form.timeline || null,
        preferred_time: form.preferred_time || null,
      });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Could not submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-10 text-center" data-testid={`${testIdPrefix}-success`}>
        <Check className="w-12 h-12 text-emerald-600 mx-auto mb-3" strokeWidth={2.5} />
        <h3 className="text-2xl font-bold text-gray-900">{successTitle || 'Thank you!'}</h3>
        <p className="text-gray-600 mt-2">A ZHELTA Systems specialist will get back to you within 1 business day.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-gray-200 bg-white p-6 md:p-8 space-y-4" data-testid={`${testIdPrefix}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Full name" required value={form.name} onChange={set('name')} testId={`${testIdPrefix}-name`} />
        <Field label="Work email" type="email" required value={form.email} onChange={set('email')} testId={`${testIdPrefix}-email`} />
      </div>
      {fields.includes('company') && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Company" value={form.company} onChange={set('company')} testId={`${testIdPrefix}-company`} />
          {fields.includes('phone') && <Field label="Phone" type="tel" value={form.phone} onChange={set('phone')} testId={`${testIdPrefix}-phone`} />}
        </div>
      )}
      {fields.includes('budget') && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Estimated budget" value={form.budget} onChange={set('budget')} placeholder="e.g. $2k–$5k" testId={`${testIdPrefix}-budget`} />
          <Field label="Timeline" value={form.timeline} onChange={set('timeline')} placeholder="e.g. Within 30 days" testId={`${testIdPrefix}-timeline`} />
        </div>
      )}
      {fields.includes('preferred_time') && (
        <Field label="Preferred time for the call" value={form.preferred_time} onChange={set('preferred_time')} placeholder="e.g. Mornings EST" testId={`${testIdPrefix}-preferred-time`} />
      )}
      <div>
        <label className="block text-[12px] font-semibold uppercase tracking-wider text-gray-600 mb-1.5">Message{fields.includes('message') ? ' *' : ''}</label>
        <textarea
          value={form.message}
          onChange={set('message')}
          required={fields.includes('message')}
          rows={4}
          data-testid={`${testIdPrefix}-message`}
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#f5a623] focus:ring-2 focus:ring-amber-100 outline-none transition"
        />
      </div>
      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
      <button
        type="submit"
        disabled={submitting}
        data-testid={`${testIdPrefix}-submit`}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#f5a623] text-black text-[13px] font-bold uppercase tracking-wider hover:brightness-110 transition disabled:opacity-60"
      >
        {submitting ? 'Sending…' : <>{ctaLabel} <Send className="w-4 h-4" /></>}
      </button>
    </form>
  );
}

function Field({ label, value, onChange, type = 'text', required = false, placeholder, testId }) {
  return (
    <div>
      <label className="block text-[12px] font-semibold uppercase tracking-wider text-gray-600 mb-1.5">{label}{required && ' *'}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#f5a623] focus:ring-2 focus:ring-amber-100 outline-none transition"
      />
    </div>
  );
}
